import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import Header from "@/components/Header";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QUESTIONS = [
  {
    id: "fitness_level",
    question: "WHAT IS YOUR CURRENT FITNESS LEVEL?",
    description: "Be honest - this helps us match you with the right program.",
    options: [
      { value: "beginner", label: "BEGINNER", description: "New to fitness or returning after a long break" },
      { value: "intermediate", label: "INTERMEDIATE", description: "Regular workout routine for 6+ months" },
      { value: "advanced", label: "ADVANCED", description: "Consistent training for 2+ years" }
    ]
  },
  {
    id: "primary_goal",
    question: "WHAT IS YOUR PRIMARY FITNESS GOAL?",
    description: "Choose the goal that matters most to you right now.",
    options: [
      { value: "weight_loss", label: "WEIGHT LOSS", description: "Burn fat and get leaner" },
      { value: "muscle_gain", label: "MUSCLE GAIN", description: "Build strength and muscle mass" },
      { value: "endurance", label: "ENDURANCE", description: "Improve cardiovascular fitness" },
      { value: "flexibility", label: "FLEXIBILITY", description: "Enhance mobility and flexibility" },
      { value: "general_fitness", label: "GENERAL FITNESS", description: "Overall health and wellness" },
      { value: "athletic_performance", label: "ATHLETIC PERFORMANCE", description: "Sport-specific training" }
    ]
  },
  {
    id: "workout_frequency",
    question: "HOW OFTEN CAN YOU WORKOUT?",
    description: "Consider your schedule and commitments.",
    options: [
      { value: "1-2", label: "1-2 DAYS", description: "Per week" },
      { value: "3-4", label: "3-4 DAYS", description: "Per week" },
      { value: "5+", label: "5+ DAYS", description: "Per week" }
    ]
  },
  {
    id: "workout_duration",
    question: "HOW LONG CAN YOU WORKOUT?",
    description: "Average time available per session.",
    options: [
      { value: "30", label: "30 MINUTES", description: "Quick and efficient" },
      { value: "45", label: "45 MINUTES", description: "Balanced session" },
      { value: "60", label: "60+ MINUTES", description: "Full comprehensive workout" }
    ]
  },
  {
    id: "equipment_access",
    question: "WHERE WILL YOU BE TRAINING?",
    description: "This affects your program design.",
    options: [
      { value: "home", label: "HOME", description: "Limited or no equipment" },
      { value: "gym", label: "GYM", description: "Full gym access" },
      { value: "both", label: "BOTH", description: "Flexible training locations" }
    ]
  }
];

const AssessmentPage = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({
    fitness_level: "",
    primary_goal: "",
    workout_frequency: "",
    workout_duration: "",
    equipment_access: "",
    health_conditions: ""
  });
  const [loading, setLoading] = useState(false);

  const currentQuestion = QUESTIONS[currentStep];
  const progress = ((currentStep + 1) / QUESTIONS.length) * 100;
  const canProceed = answers[currentQuestion?.id] !== "";

  const handleSelect = (value) => {
    setAnswers({ ...answers, [currentQuestion.id]: value });
  };

  const handleNext = () => {
    if (currentStep < QUESTIONS.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/assessment`, answers);
      navigate(`/results/${response.data.recommended_program}`);
    } catch (error) {
      console.error("Error submitting assessment:", error);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <Header />
      
      <main className="pt-24 pb-16 px-6 md:px-12">
        <div className="max-w-2xl mx-auto">
          {/* Progress Bar */}
          <div className="mb-12">
            <div className="flex justify-between items-center mb-2">
              <span className="font-oswald uppercase tracking-wider text-sm text-neutral-500">
                STEP {currentStep + 1} OF {QUESTIONS.length}
              </span>
              <span className="font-oswald uppercase tracking-wider text-sm text-neutral-500">
                {Math.round(progress)}%
              </span>
            </div>
            <div className="progress-bar">
              <div 
                className="progress-bar-fill" 
                style={{ width: `${progress}%` }}
                data-testid="progress-bar"
              ></div>
            </div>
          </div>

          {/* Question */}
          <div className="mb-12 animate-fade-in" key={currentStep}>
            <h1 
              className="font-oswald text-3xl md:text-4xl font-bold uppercase tracking-tighter mb-4"
              data-testid="question-title"
            >
              {currentQuestion.question}
            </h1>
            <p className="font-manrope text-neutral-400 text-lg">
              {currentQuestion.description}
            </p>
          </div>

          {/* Options */}
          <div className="space-y-4 mb-12">
            {currentQuestion.options.map((option) => (
              <button
                key={option.value}
                onClick={() => handleSelect(option.value)}
                className={`w-full text-left quiz-option ${
                  answers[currentQuestion.id] === option.value ? 'selected' : ''
                }`}
                data-testid={`option-${option.value}`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className={`font-oswald text-lg uppercase tracking-tighter ${
                      answers[currentQuestion.id] === option.value ? 'text-black' : 'text-white'
                    }`}>
                      {option.label}
                    </p>
                    <p className={`font-manrope text-sm ${
                      answers[currentQuestion.id] === option.value ? 'text-neutral-600' : 'text-neutral-500'
                    }`}>
                      {option.description}
                    </p>
                  </div>
                  {answers[currentQuestion.id] === option.value && (
                    <Check size={24} className="text-black" />
                  )}
                </div>
              </button>
            ))}
          </div>

          {/* Navigation */}
          <div className="flex justify-between items-center">
            <button
              onClick={handleBack}
              disabled={currentStep === 0}
              className={`flex items-center gap-2 font-oswald uppercase tracking-wider ${
                currentStep === 0 ? 'text-neutral-700 cursor-not-allowed' : 'text-white hover:text-neutral-400'
              }`}
              data-testid="back-btn"
            >
              <ArrowLeft size={20} />
              BACK
            </button>
            
            <button
              onClick={handleNext}
              disabled={!canProceed || loading}
              className={`btn-primary flex items-center gap-2 ${
                !canProceed ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              data-testid="next-btn"
            >
              {loading ? (
                "PROCESSING..."
              ) : currentStep === QUESTIONS.length - 1 ? (
                <>GET MY PROGRAM</>
              ) : (
                <>
                  NEXT
                  <ArrowRight size={20} />
                </>
              )}
            </button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AssessmentPage;
